// NOTE: NPR One SDK removed due to security vulnerabilities - using Wikipedia CSV data
// import { NprOneSDK } from '@npr/npr-one-sdk';

export class DataProcessor {
    constructor() {
        this.stationsData = [];
        this.electionData = null;
        this.countyShapefile = null;
        // this.nprSDK = null; // Removed due to security vulnerabilities
        this.collectedStations = new Set(); // Track unique stations to avoid duplicates
    }

    async loadStationData() {
        console.log('🚀 Starting NPR station data loading...');
        
        try {
            // Primary approach: Load Wikipedia scraped data first
            console.log('🔍 Attempting to load comprehensive Wikipedia station data...');
            await this.loadStationsFromWikipedia();
            
            // If we get here, Wikipedia data loaded successfully
            console.log(`✅ Successfully loaded ${this.stationsData.length} stations from Wikipedia!`);
            return;
            
        } catch (wikipediaError) {
            console.warn('⚠️ Wikipedia CSV data failed, trying fallback approaches...', wikipediaError.message);
            
            try {
                // Fallback: Try comprehensive state-by-state collection 
                console.log('🔍 Trying comprehensive NPR API collection...');
                await this.loadStationsComprehensive();
                
            } catch (comprehensiveError) {
                console.warn('⚠️ Comprehensive collection failed, trying basic SDK...', comprehensiveError.message);
                
                try {
                    // Fallback to basic SDK search
                    await this.loadStationsWithSDK();
                    
                } catch (sdkError) {
                    console.warn('⚠️ NPR One SDK failed, trying direct API...', sdkError.message);
                    
                    try {
                        // Fallback to direct API call
                        const response = await fetch('https://api.npr.org/stations/v2/search');
                        
                        if (!response.ok) {
                            throw new Error(`HTTP error! status: ${response.status}`);
                        }
                        
                        const data = await response.json();
                        this.stationsData = this.processStationData(data);
                        console.log(`✅ Loaded ${this.stationsData.length} NPR stations from direct API`);
                        
                    } catch (directError) {
                        console.error('❌ All API approaches failed, using sample data as final fallback:', directError.message);
                        // Ultimate fallback to comprehensive sample data
                        this.stationsData = this.getSampleStationData();
                        console.log(`⚠️ Using ${this.stationsData.length} sample NPR stations as fallback`);
                    }
                }
            }
        }
    }

    /**
     * Load NPR stations from Wikipedia scraped CSV data
     */
    async loadStationsFromWikipedia() {
        try {
            console.log('🔄 Loading NPR stations from Wikipedia CSV with funding data...');
            
            // Try different paths for the Wikipedia CSV file with funding data
            const possiblePaths = [
                '/sponsor_an_NPR/data/npr-stations-wikipedia-with-funding.csv',
                '/data/npr-stations-wikipedia-with-funding.csv',
                './data/npr-stations-wikipedia-with-funding.csv',
                '/public/data/npr-stations-wikipedia-with-funding.csv',
                // Fallback to original CSV if funding version not found
                '/sponsor_an_NPR/data/npr-stations-wikipedia.csv',
                '/data/npr-stations-wikipedia.csv', 
                './data/npr-stations-wikipedia.csv',
                '/public/data/npr-stations-wikipedia.csv'
            ];
            
            let response;
            let csvText;
            let workingPath;
            
            for (const path of possiblePaths) {
                try {
                    console.log(`  Trying path: ${path}`);
                    response = await fetch(path);
                    
                    if (response.ok) {
                        csvText = await response.text();
                        workingPath = path;
                        console.log(`  ✅ Successfully loaded from: ${path}`);
                        break;
                    }
                } catch (pathError) {
                    console.log(`  ❌ Failed to load from ${path}: ${pathError.message}`);
                    continue;
                }
            }
            
            if (!csvText) {
                throw new Error('Could not load CSV from any known path');
            }
            
                        const stations = this.parseStationsCSV(csvText, true); // Always expect validation data in our Wikipedia CSV // Always expect validation data now
            
            if (stations && stations.length > 0) {
                let filteredStations = stations.map(station => ({
                    id: station.callSign,
                    name: station.callSign, // Use call sign as name
                    callSign: station.callSign,
                    frequency: station.frequency,
                    format: station.format, 
                    city: station.city,
                    state: station.state,
                    latitude: station.transmitterCoords ? this.parseCoordinates(station.transmitterCoords)?.[0] : null,
                    longitude: station.transmitterCoords ? this.parseCoordinates(station.transmitterCoords)?.[1] : null,
                    coordinates: station.transmitterCoords ? this.parseCoordinates(station.transmitterCoords) : null,
                    website: station.website,
                    urls: station.website ? [{ href: station.website }] : [],
                    donationUrl: station.website || '',
                    programmingFormat: this.capitalizeWords(station.programmingFormat),
                    affiliations: station.affiliations,
                    workingUrl: station.workingUrl,
                    urlStatus: station.urlStatus,
                    // Add funding data
                    federalSupport: station.federalSupport,
                    stateGDP: station.stateGDP,
                    politicalLeaning: station.politicalLeaning,
                    fundingType: station.fundingType,
                    fundingAffiliation: station.fundingAffiliation,
                    description: `${station.callSign} - ${station.frequency} serving ${station.city}, ${station.state}`,
                    type: 'npr',
                    source: 'Wikipedia'
                }));
                
                // Filter stations based on criteria
                const originalCount = filteredStations.length;
                
                // First, only include stations with coordinates
                filteredStations = filteredStations.filter(station => station.latitude && station.longitude);
                
                // Filter out stations with non-working URLs (only show accessible ones)
                filteredStations = filteredStations.filter(station => station.workingUrl === 'true');
                console.log(`   Filtered out ${originalCount - filteredStations.length} stations with non-working URLs or missing coordinates`);
                
                this.stationsData = filteredStations;
                
                console.log(`✅ Successfully loaded ${this.stationsData.length} NPR stations from Wikipedia CSV with URL validation and funding data (${workingPath})`);
                console.log(`   Total stations in CSV: ${originalCount}`);
                console.log(`   Stations with coordinates and working URLs: ${this.stationsData.length}`);
                const workingUrlCount = stations.filter(s => s.workingUrl === 'true').length;
                console.log(`   Total stations with working URLs: ${workingUrlCount}`);
                
                // Log funding data statistics
                const stationsWithFunding = this.stationsData.filter(s => s.federalSupport && s.federalSupport !== '');
                console.log(`   Stations with federal funding data: ${stationsWithFunding.length}`);
                
                if (stationsWithFunding.length > 0) {
                    const fundingPercentages = stationsWithFunding
                        .map(s => parseFloat(s.federalSupport.replace('%', '')))
                        .filter(p => !isNaN(p));
                    
                    if (fundingPercentages.length > 0) {
                        const avgFunding = fundingPercentages.reduce((a, b) => a + b, 0) / fundingPercentages.length;
                        const maxFunding = Math.max(...fundingPercentages);
                        const minFunding = Math.min(...fundingPercentages);
                        console.log(`   Federal funding range: ${minFunding.toFixed(1)}% - ${maxFunding.toFixed(1)}% (avg: ${avgFunding.toFixed(1)}%)`);
                    }
                }
                return this.stationsData;
            }
            
            throw new Error('No stations found in Wikipedia CSV data');
            
        } catch (error) {
            console.error('❌ Failed to load Wikipedia CSV station data:', error.message);
            throw error;
        }
    }

    /**
     * Parse CSV content into station objects
     */
    parseStationsCSV(csvText, isValidated = false) {
        try {
            const lines = csvText.trim().split('\n');
            if (lines.length < 2) {
                throw new Error('CSV file appears to be empty or invalid');
            }
            
            // Skip header row
            const dataLines = lines.slice(1);
            const stations = [];
            
            for (const line of dataLines) {
                // Parse CSV line - handle quoted fields
                const fields = this.parseCSVLine(line);
                
                if (fields.length >= 7) {
                    const station = {
                        callSign: fields[0].replace(/"/g, '').trim(),
                        frequency: fields[1].replace(/"/g, '').trim(),
                        format: fields[2].replace(/"/g, '').trim(),
                        city: fields[3].replace(/"/g, '').trim(),
                        state: fields[4].replace(/"/g, '').trim(),
                        transmitterCoords: fields[5].replace(/"/g, '').trim(),
                        website: fields[6].replace(/"/g, '').trim(),
                        programmingFormat: fields[7] ? fields[7].replace(/"/g, '').trim() : '',
                        affiliations: fields[8] ? fields[8].replace(/"/g, '').trim() : ''
                    };
                    
                    // Add validation fields (now always available in our Wikipedia CSV)
                    if (fields.length >= 11) {
                        station.workingUrl = fields[9] ? fields[9].replace(/"/g, '').trim() : '';
                        station.urlStatus = fields[10] ? fields[10].replace(/"/g, '').trim() : '';
                    }
                    
                    // Add funding data fields if available (columns 11-15)
                    if (fields.length >= 16) {
                        station.federalSupport = fields[11] ? fields[11].replace(/"/g, '').trim() : '';
                        station.stateGDP = fields[12] ? fields[12].replace(/"/g, '').trim() : '';
                        station.politicalLeaning = fields[13] ? fields[13].replace(/"/g, '').trim() : '';
                        station.fundingType = fields[14] ? fields[14].replace(/"/g, '').trim() : '';
                        station.fundingAffiliation = fields[15] ? fields[15].replace(/"/g, '').trim() : '';
                    }
                    
                    stations.push(station);
                }
            }
            
            console.log(`Parsed ${stations.length} stations from Wikipedia CSV with validation data`);
            const workingCount = stations.filter(s => s.workingUrl === 'true').length;
            console.log(`  - ${workingCount} stations have working URLs`);
            const invalidCount = stations.filter(s => s.workingUrl === 'false').length;
            console.log(`  - ${invalidCount} stations have invalid/non-working URLs`);
            return stations;
            
        } catch (error) {
            console.error('Error parsing CSV:', error);
            return [];
        }
    }
    
    /**
     * Parse a CSV line handling quoted fields properly
     */
    parseCSVLine(line) {
        const fields = [];
        let current = '';
        let inQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
            const char = line[i];
            
            if (char === '"') {
                inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
                fields.push(current);
                current = '';
            } else {
                current += char;
            }
        }
        
        // Add the last field
        fields.push(current);
        return fields;
    }
    
    /**
     * Parse coordinate string into [lat, lon] array
     */
    parseCoordinates(coordString) {
        if (!coordString || coordString.trim() === '') {
            return null;
        }
        
        try {
            const coords = coordString.split(',').map(c => parseFloat(c.trim()));
            if (coords.length === 2 && !isNaN(coords[0]) && !isNaN(coords[1])) {
                return coords;
            }
        } catch (error) {
            console.warn('Failed to parse coordinates:', coordString);
        }
        
        return null;
    }

    /**
     * Capitalize the first letter of each word in a string
     */
    capitalizeWords(str) {
        if (!str || typeof str !== 'string') {
            return str;
        }
        
        return str.split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    }



    async loadStationsComprehensive() {
        // Initialize NPR One SDK
        this.nprSDK = new NprOneSDK();
        
        // Configure SDK (minimal config for public station data)
        this.nprSDK.config = {
            apiBaseUrl: 'https://api.npr.org'
        };

        // US States and territories for comprehensive collection
        const states = [
            'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 
            'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 
            'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 
            'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 
            'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 
            'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 
            'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 
            'Wisconsin', 'Wyoming', 'District of Columbia'
        ];

        const allStations = [];
        let successfulStates = 0;
        let totalAttempts = 0;

        console.log(`🌎 Querying ${states.length} states for NPR stations...`);

        // Query each state with rate limiting
        for (const state of states) {
            try {
                totalAttempts++;
                console.log(`🔍 Searching ${state}... (${totalAttempts}/${states.length})`);
                
                // Add small delay to avoid overwhelming the API
                if (totalAttempts > 1) {
                    await this.delay(500); // 500ms delay between requests
                }
                
                const stateStations = await this.nprSDK.searchStationsByState(state);
                
                if (stateStations && stateStations.length > 0) {
                    successfulStates++;
                    
                    // Filter out duplicates using station ID
                    const newStations = stateStations.filter(station => {
                        const stationKey = station.id || `${station.attributes?.call}-${station.attributes?.city}`;
                        if (!this.collectedStations.has(stationKey)) {
                            this.collectedStations.add(stationKey);
                            return true;
                        }
                        return false;
                    });
                    
                    allStations.push(...newStations);
                    console.log(`  ✅ ${state}: Found ${stateStations.length} stations (${newStations.length} new)`);
                } else {
                    console.log(`  ⚪ ${state}: No stations found`);
                }
                
            } catch (stateError) {
                console.warn(`  ❌ ${state}: Search failed -`, stateError.message);
                // Continue with other states even if one fails
            }
        }

        console.log(`\n📊 Collection Summary:`);
        console.log(`   States successfully queried: ${successfulStates}/${states.length}`);
        console.log(`   Total unique stations found: ${allStations.length}`);

        if (allStations.length > 0) {
            // Process all collected stations
            this.stationsData = this.processSDKStationData(allStations);
            console.log(`✅ Final dataset: ${this.stationsData.length} stations ready for map`);
        } else {
            throw new Error('No stations collected from any state');
        }
    }

    async loadStationsWithSDK() {
        // Initialize NPR One SDK (fallback method)
        this.nprSDK = new NprOneSDK();
        
        // Configure SDK (minimal config for public station data)
        this.nprSDK.config = {
            apiBaseUrl: 'https://api.npr.org'
        };
        
        // Search for all stations (no query returns geographically closest stations)
        const stations = await this.nprSDK.searchStations();
        
        // Process SDK station data
        this.stationsData = this.processSDKStationData(stations);
        
        console.log(`Loaded ${this.stationsData.length} NPR stations via basic SDK`);
    }

    // Helper method for delays
    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    processSDKStationData(stations) {
        if (!stations || !Array.isArray(stations)) {
            return this.getSampleStationData();
        }

        return stations
            .filter(station => station && station.attributes)
            .map(station => {
                const attrs = station.attributes;
                return {
                    id: station.id,
                    call: attrs.call || station.id,
                    name: attrs.name || 'NPR Station',
                    latitude: parseFloat(attrs.lat || attrs.latitude),
                    longitude: parseFloat(attrs.lon || attrs.longitude),
                    city: attrs.city,
                    state: attrs.state,
                    phone: attrs.phone,
                    urls: attrs.urls || [{ href: `https://www.npr.org/stations/${station.id}` }],
                    donationUrl: attrs.donationUrl || `https://www.npr.org/stations/${station.id}/donate`,
                    market_city: attrs.market_city,
                    frequency: attrs.frequency,
                    band: attrs.band,
                    status: attrs.status || 'active'
                };
            })
            .filter(station => 
                station.latitude && station.longitude &&
                station.latitude >= 24 && station.latitude <= 49 && 
                station.longitude >= -125 && station.longitude <= -66
            ); // Filter to continental US
    }

    processStationData(apiData) {
        if (!apiData || !apiData.items) {
            return this.getSampleStationData();
        }

        return apiData.items
            .filter(station => station.attributes && station.attributes.lat && station.attributes.lon)
            .map(station => {
                const attrs = station.attributes;
                return {
                    id: station.id,
                    call: attrs.call || station.href?.split('/').pop()?.toUpperCase(),
                    name: attrs.name || attrs.brand?.name || 'NPR Station',
                    latitude: parseFloat(attrs.lat),
                    longitude: parseFloat(attrs.lon),
                    city: attrs.city,
                    state: attrs.state,
                    phone: attrs.phone,
                    urls: attrs.urls || [],
                    donationUrl: attrs.donationUrl,
                    market_city: attrs.market_city,
                    frequency: attrs.frequency,
                    band: attrs.band,
                    status: attrs.status || 'active'
                };
            })
            .filter(station => 
                station.latitude >= 24 && station.latitude <= 49 && 
                station.longitude >= -125 && station.longitude <= -66
            ); // Filter to continental US
    }

    getSampleStationData() {
        // Comprehensive sample NPR stations covering all major markets and at-risk rural stations
        return [
            {
                id: '1',
                call: 'WAMU',
                name: 'American University Radio',
                latitude: 38.9072,
                longitude: -77.0369,
                city: 'Washington',
                state: 'DC',
                phone: '(202) 885-1200',
                urls: [{ href: 'https://wamu.org' }],
                donationUrl: 'https://wamu.org/donate'
            },
            {
                id: '2',
                call: 'KQED',
                name: 'KQED Public Radio',
                latitude: 37.7749,
                longitude: -122.4194,
                city: 'San Francisco',
                state: 'CA',
                phone: '(415) 864-2000',
                urls: [{ href: 'https://kqed.org' }],
                donationUrl: 'https://kqed.org/donate'
            },
            {
                id: '3',
                call: 'WNYC',
                name: 'New York Public Radio',
                latitude: 40.7128,
                longitude: -74.0060,
                city: 'New York',
                state: 'NY',
                phone: '(212) 669-7800',
                urls: [{ href: 'https://wnyc.org' }],
                donationUrl: 'https://wnyc.org/donate'
            },
            {
                id: '4',
                call: 'WBEZ',
                name: 'Chicago Public Media',
                latitude: 41.8781,
                longitude: -87.6298,
                city: 'Chicago',
                state: 'IL',
                phone: '(312) 948-4600',
                urls: [{ href: 'https://wbez.org' }],
                donationUrl: 'https://wbez.org/donate'
            },
            {
                id: '5',
                call: 'KUT',
                name: 'KUT & KUTX Austin',
                latitude: 30.2672,
                longitude: -97.7431,
                city: 'Austin',
                state: 'TX',
                phone: '(512) 471-1631',
                urls: [{ href: 'https://kut.org' }],
                donationUrl: 'https://kut.org/donate'
            },
            {
                id: '6',
                call: 'WGBH',
                name: 'GBH Boston',
                latitude: 42.3601,
                longitude: -71.0589,
                city: 'Boston',
                state: 'MA',
                phone: '(617) 300-2000',
                urls: [{ href: 'https://wgbh.org' }],
                donationUrl: 'https://wgbh.org/donate'
            },
            {
                id: '7',
                call: 'KPCC',
                name: 'LAist 89.3',
                latitude: 34.0522,
                longitude: -118.2437,
                city: 'Los Angeles',
                state: 'CA',
                phone: '(626) 583-5100',
                urls: [{ href: 'https://laist.com' }],
                donationUrl: 'https://laist.com/donate'
            },
            {
                id: '8',
                call: 'KUOW',
                name: 'KUOW Seattle',
                latitude: 47.6062,
                longitude: -122.3321,
                city: 'Seattle',
                state: 'WA',
                phone: '(206) 543-2710',
                urls: [{ href: 'https://kuow.org' }],
                donationUrl: 'https://kuow.org/donate'
            },
            {
                id: '9',
                call: 'WFPL',
                name: 'Louisville Public Media',
                latitude: 38.2527,
                longitude: -85.7585,
                city: 'Louisville',
                state: 'KY',
                phone: '(502) 814-6500',
                urls: [{ href: 'https://lpm.org' }],
                donationUrl: 'https://lpm.org/donate'
            },
            {
                id: '10',
                call: 'WOSU',
                name: 'WOSU Public Media',
                latitude: 39.9612,
                longitude: -82.9988,
                city: 'Columbus',
                state: 'OH',
                phone: '(614) 292-9678',
                urls: [{ href: 'https://wosu.org' }],
                donationUrl: 'https://wosu.org/donate'
            },
            
            // Additional Major Markets & At-Risk Stations
            {
                id: '11',
                call: 'WABE',
                name: 'Atlanta Public Broadcasting',
                latitude: 33.7490,
                longitude: -84.3880,
                city: 'Atlanta',
                state: 'GA',
                phone: '(404) 685-2800',
                urls: [{ href: 'https://wabe.org' }],
                donationUrl: 'https://wabe.org/donate'
            },
            {
                id: '12',
                call: 'WWNO',
                name: 'New Orleans Public Radio',
                latitude: 29.9511,
                longitude: -90.0715,
                city: 'New Orleans',
                state: 'LA',
                phone: '(504) 280-7000',
                urls: [{ href: 'https://wwno.org' }],
                donationUrl: 'https://wwno.org/donate'
            },
            {
                id: '13',
                call: 'OPB',
                name: 'Oregon Public Broadcasting',
                latitude: 45.5152,
                longitude: -122.6784,
                city: 'Portland',
                state: 'OR',
                phone: '(503) 293-1982',
                urls: [{ href: 'https://opb.org' }],
                donationUrl: 'https://opb.org/donate'
            },
            {
                id: '14',
                call: 'MPR',
                name: 'Minnesota Public Radio',
                latitude: 44.9537,
                longitude: -93.0900,
                city: 'St. Paul',
                state: 'MN',
                phone: '(651) 290-1212',
                urls: [{ href: 'https://mprnews.org' }],
                donationUrl: 'https://mprnews.org/donate'
            },
            {
                id: '15',
                call: 'KPBS',
                name: 'San Diego Public Broadcasting',
                latitude: 32.7157,
                longitude: -117.1611,
                city: 'San Diego',
                state: 'CA',
                phone: '(619) 594-1515',
                urls: [{ href: 'https://kpbs.org' }],
                donationUrl: 'https://kpbs.org/donate'
            },
            
            // Rural & Tribal Stations Most At Risk
            {
                id: '16',
                call: 'KTNA',
                name: 'Talkeetna Public Radio',
                latitude: 62.3200,
                longitude: -150.1061,
                city: 'Talkeetna',
                state: 'AK',  
                phone: '(907) 733-1700',
                urls: [{ href: 'https://ktna.org' }],
                donationUrl: 'https://ktna.org/donate'
            },
            {
                id: '17',
                call: 'KYUK',
                name: 'Bethel Broadcasting',
                latitude: 60.7922,
                longitude: -161.7558,
                city: 'Bethel',
                state: 'AK',
                phone: '(907) 543-3131',
                urls: [{ href: 'https://kyuk.org' }],
                donationUrl: 'https://kyuk.org/donate'
            },
            {
                id: '18',
                call: 'KRCC',
                name: 'Colorado College Public Radio',
                latitude: 38.8339,
                longitude: -104.8214,
                city: 'Colorado Springs',
                state: 'CO',
                phone: '(719) 473-4801',
                urls: [{ href: 'https://krcc.org' }],
                donationUrl: 'https://krcc.org/donate'
            },
            {
                id: '19',
                call: 'VPR',
                name: 'Vermont Public Radio',
                latitude: 44.2601,
                longitude: -72.5806,
                city: 'Montpelier',
                state: 'VT',
                phone: '(802) 229-5700',
                urls: [{ href: 'https://vpr.org' }],
                donationUrl: 'https://vpr.org/donate'
            },
            {
                id: '20',
                call: 'WPLN',
                name: 'Nashville Public Radio',
                latitude: 36.1627,
                longitude: -86.7816,
                city: 'Nashville',
                state: 'TN',
                phone: '(615) 760-2800',
                urls: [{ href: 'https://wpln.org' }],
                donationUrl: 'https://wpln.org/donate'
            },
            {
                id: '21',
                call: 'WFAE',
                name: 'Charlotte Public Radio',
                latitude: 35.2271,
                longitude: -80.8431,
                city: 'Charlotte',
                state: 'NC',
                phone: '(704) 549-9323',
                urls: [{ href: 'https://wfae.org' }],
                donationUrl: 'https://wfae.org/donate'
            },
            {
                id: '22',
                call: 'WESA',
                name: 'Pittsburgh Public Media',
                latitude: 40.4406,
                longitude: -79.9959,
                city: 'Pittsburgh',
                state: 'PA',
                phone: '(412) 622-1300',
                urls: [{ href: 'https://wesa.fm' }],
                donationUrl: 'https://wesa.fm/donate'
            },
            {
                id: '23',
                call: 'IPR',
                name: 'Iowa Public Radio',
                latitude: 41.5868,
                longitude: -93.6250,
                city: 'Des Moines',
                state: 'IA',
                phone: '(515) 725-1700',
                urls: [{ href: 'https://iowapublicradio.org' }],
                donationUrl: 'https://iowapublicradio.org/donate'
            },
            {
                id: '24',
                call: 'WFYI',
                name: 'Metropolitan Indianapolis Public Media',
                latitude: 39.7684,
                longitude: -86.1581,
                city: 'Indianapolis',
                state: 'IN',
                phone: '(317) 636-2020',
                urls: [{ href: 'https://wfyi.org' }],
                donationUrl: 'https://wfyi.org/donate'
            },
            {
                id: '25',
                call: 'KUNC',
                name: 'University of Northern Colorado Public Radio',
                latitude: 40.4233,
                longitude: -104.7091,
                city: 'Greeley',
                state: 'CO',
                phone: '(970) 351-4001',
                urls: [{ href: 'https://kunc.org' }],
                donationUrl: 'https://kunc.org/donate'
            }
        ];
    }    async processElectionData() {
        try {
            // Load the processed GeoJSON file
            const response = await fetch('/sponsor_an_NPR/data/counties-election-results-simplified.geojson');
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            this.electionData = await response.json();
            
            console.log(`Loaded ${this.electionData.features.length} county features with election data`);
            
        } catch (error) {
            console.error('Error processing election data:', error);
            this.electionData = null;
        }
    }

    async loadCSV(filepath) {
        try {
            const response = await fetch(filepath);
            const text = await response.text();
            
            return this.parseCSV(text);
        } catch (error) {
            console.error('Error loading CSV:', error);
            return [];
        }
    }

    parseCSV(text) {
        const lines = text.trim().split('\n');
        const headers = lines[0].split(',').map(h => h.trim());
        
        return lines.slice(1).map(line => {
            const values = line.split(',');
            const obj = {};
            
            headers.forEach((header, index) => {
                let value = values[index]?.trim() || '';
                
                // Try to parse as number
                if (value && !isNaN(value)) {
                    value = parseFloat(value);
                }
                
                obj[header] = value;
            });
            
            return obj;
        });
    }

    createElectionGeoJSON(csvData) {
        // This is a simplified version - in reality, you'd merge with actual county geometries
        // For now, we'll create point features for each county
        const features = csvData.map(county => ({
            type: 'Feature',
            properties: {
                county_fips: county.county_fips,
                county_name: county.county_name,
                state_name: county.state_name,
                votes_gop: county.votes_gop,
                votes_dem: county.votes_dem,
                total_votes: county.total_votes,
                per_gop: county.per_gop,
                per_dem: county.per_dem,
                per_point_diff: county.per_point_diff
            },
            geometry: {
                type: 'Point',
                coordinates: [0, 0] // Would need actual county centroids
            }
        }));

        return {
            type: 'FeatureCollection',
            features: features
        };
    }

    getStationsData() {
        return this.stationsData;
    }

    getElectionData() {
        return this.electionData;
    }

    // Method to convert shapefile to PMTiles (would need server-side processing)
    async convertShapefileToGeoJSON() {
        // This would typically be done server-side or with a conversion tool
        // For now, returning null to indicate no conversion available
        return null;
    }
}
